import { useTranslation } from "react-i18next";
import {
  Box,
  Button,
  Chip,
  Collapse,
  TableCell,
  TableRow,
  Typography,
  makeStyles,
} from "@material-ui/core";
import BugReportIcon from "@material-ui/icons/BugReport";
import InsertDriveFileIcon from "@material-ui/icons/InsertDriveFile";
import ExpandMoreIcon from "@material-ui/icons/ExpandMore";
import CheckCircleOutlineIcon from "@material-ui/icons/CheckCircleOutline";
import BlockIcon from "@material-ui/icons/Block";
import dayjs from "dayjs";
import TruncatedText from "components/TruncatedText";
import type { MalwareContent, MalwareFile } from "@dashboard-v3/api";
import ContentFiles from "./ContentFiles";
import { isNew, shortChecksum } from "../utils";

interface Props {
  content: MalwareContent;
  expanded: boolean;
  files: MalwareFile[];
  loadingFiles: boolean;
  onToggle: (content: MalwareContent) => void;
  onShowAllFiles: (content: MalwareContent) => void;
  onFileClick: (file: MalwareFile, content: MalwareContent) => void;
  onRelease: (content: MalwareContent) => void;
}

export default function ThreatRow({
  content,
  expanded,
  files,
  loadingFiles,
  onToggle,
  onShowAllFiles,
  onFileClick,
  onRelease,
}: Props) {
  const { t } = useTranslation("mxheroShares");
  const classes = useStyles();
  const unread = isNew(content);
  const released = content.allowedToUse;
  const iconClass = classNames(
    classes.threatIcon,
    released && classes.released
  );
  const rowClass = classNames(
    classes.row,
    unread && classes.unread,
    expanded && classes.open
  );
  const toggleClass = classNames(
    classes.toggle,
    expanded && classes.toggleOpen
  );

  const detected = content.detectedAt
    ? dayjs(content.detectedAt).fromNow()
    : t("malware.list.unknownDate");

  const detectedFull = content.detectedAt
    ? dayjs(content.detectedAt).format("YYYY-MM-DD HH:mm")
    : "";

  const statusDetail = released
    ? t("malware.list.ignoredBy", { user: content.allowedBy })
    : content.viewedBy
    ? t("malware.list.viewedBy", { user: content.viewedBy })
    : "";

  return (
    <>
      <TableRow
        hover
        className={rowClass}
        data-testid="malware-content"
        role="button"
        tabIndex={0}
        aria-expanded={expanded}
        onClick={() => onToggle(content)}
        onKeyDown={event => {
          if (event.key === "Enter" || event.key === " ") {
            event.preventDefault();
            onToggle(content);
          }
        }}
      >
        <TableCell className={classes.marker}>
          <span className={classes.dot} />
        </TableCell>
        <TableCell>
          <Box display="flex" alignItems="center" gridGap={12} minWidth={0}>
            <div className={iconClass}>
              {released ? (
                <InsertDriveFileIcon fontSize="small" />
              ) : (
                <BugReportIcon fontSize="small" />
              )}
            </div>
            <Box minWidth={0}>
              <TruncatedText
                text={content.threat}
                variant="body2"
                className={classes.threatName}
                maxWidth={360}
              />
              <Typography
                variant="caption"
                color="textSecondary"
                component="p"
                noWrap
              >
                {t("malware.list.filesCount", { count: content.filesCount })}
                <span className={classes.separator}>·</span>
                <span className={classes.mono}>
                  {shortChecksum(content.checksum)}
                </span>
              </Typography>
            </Box>
          </Box>
        </TableCell>
        <TableCell>
          <Typography variant="body2" noWrap className={classes.date}>
            {detected}
          </Typography>
          <Typography variant="caption" color="textSecondary" noWrap>
            {detectedFull}
          </Typography>
        </TableCell>
        <TableCell>
          {released ? (
            <Chip
              size="small"
              icon={<CheckCircleOutlineIcon />}
              label={t("malware.status.ignored")}
              className={classNames(classes.chip, classes.chipReleased)}
            />
          ) : (
            <Chip
              size="small"
              icon={<BlockIcon />}
              label={t("malware.status.blocked")}
              className={classNames(classes.chip, classes.chipBlocked)}
            />
          )}
          <Typography
            variant="caption"
            color="textSecondary"
            component="p"
            noWrap
          >
            {statusDetail}
          </Typography>
        </TableCell>
        <TableCell align="right" className={classes.actions}>
          {!released && (
            <Button
              size="small"
              color="primary"
              variant="outlined"
              data-testid="malware-ignore-btn"
              onClick={event => {
                event.stopPropagation();
                onRelease(content);
              }}
            >
              {t("malware.actions.ignoreThreat")}
            </Button>
          )}
        </TableCell>
        <TableCell className={classes.toggleCell}>
          <ExpandMoreIcon className={toggleClass} />
        </TableCell>
      </TableRow>
      <TableRow className={classes.filesRow}>
        <TableCell colSpan={6} className={classes.filesCell}>
          <Collapse in={expanded} unmountOnExit timeout="auto">
            <div className={classes.filesList}>
              <ContentFiles
                content={content}
                files={files}
                loading={loadingFiles}
                onShowAll={onShowAllFiles}
                onFileClick={onFileClick}
              />
            </div>
          </Collapse>
        </TableCell>
      </TableRow>
    </>
  );
}

const classNames = (...names: Array<string | false>) =>
  names.filter(Boolean).join(" ");

const useStyles = makeStyles(theme => ({
  row: { height: 64, cursor: "pointer" },
  unread: {
    backgroundColor: "rgba(0, 158, 210, 0.05)",
    "& $threatName, & $date": { fontWeight: 500 },
    "& $dot": { backgroundColor: theme.palette.primary.main },
  },
  open: { "& td": { borderBottomColor: "transparent" } },
  marker: { width: 28, paddingRight: 0 },
  dot: {
    display: "block",
    width: 8,
    height: 8,
    borderRadius: "50%",
    backgroundColor: "transparent",
  },
  threatIcon: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    width: 36,
    height: 36,
    borderRadius: 8,
    flexShrink: 0,
    backgroundColor: "#fdecea",
    color: "#b3261e",
  },
  released: { backgroundColor: "#e6f4ec", color: "#2e7d52" },
  threatName: {},
  date: {},
  separator: { margin: "0 8px", opacity: 0.5 },
  mono: {
    fontFamily: "SFMono-Regular, Consolas, Menlo, monospace",
    fontSize: "0.72rem",
  },
  chip: {
    height: 20,
    fontSize: "0.7rem",
    fontWeight: 500,
    letterSpacing: "0.04em",
    "& .MuiChip-icon": { width: 14, height: 14, marginLeft: 6 },
  },
  chipBlocked: { backgroundColor: "#fdecea", color: "#b3261e" },
  chipReleased: { backgroundColor: "#e6f4ec", color: "#2e7d52" },
  actions: { whiteSpace: "nowrap", width: "1%" },
  toggleCell: { width: 40, color: theme.palette.text.secondary },
  toggle: { transition: "transform 150ms", verticalAlign: "middle" },
  toggleOpen: { transform: "rotate(180deg)" },
  filesRow: { "& td": { padding: 0, borderBottom: 0 } },
  filesCell: { backgroundColor: "#f7f9fb" },
  filesList: { borderTop: `1px solid ${theme.palette.divider}` },
}));
